import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  LinearProgress,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  Slider,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Campaign as CampaignIcon,
  CheckCircle as CheckCircleIcon,
  LightbulbOutlined as LightbulbIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  ReceiptLong as ReceiptLongIcon,
  RocketLaunch as RocketIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  Wallet as WalletIcon,
} from "@mui/icons-material";
import axios from "axios";
import Cookies from "js-cookie";
import CryptoJS from "crypto-js";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import useCustomToast from "../hooks/useCustomToast";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const SECRET_KEY = "tabtrips_hotel_secret";
const TOKEN_COOKIE_KEY = "token";
const CPC_ADS_BASE = "https://api.tabtrips.com/olivrweb/hotelapi/CpcAds.php";

/** Tab session only: survives refresh, clears when the tab closes. */
const CPC_WIZARD_SESSION_KEY = "tt_cpc_campaign_wizard_v1";

/** Razorpay checkout key id for campaign checkout (from `.env`). */
const CPC_CHECKOUT_KEY_ID = (process.env.REACT_APP_RAZORPAY_KEY_ID || "").trim();
const MIN_CPC = 8;
const MAX_CPC = 30;
/** Minimum gap: end date must be at least this many calendar days after start (1 week). */
const MIN_CAMPAIGN_SPAN_DAYS = 7;
const DEFAULT_CPC = 18;
const DEFAULT_CLICKS = 250;
const MIN_CLICKS = 50;
const MAX_CLICKS = 500;
const GST_PERCENT = 18;
const RECOMMENDED_CPC = 18;

const steps = ["Campaign details", "Payments", "Launch"];

/** Collapsed sidebar width — must match `MainLayout` main content `marginLeft`. */
const MAIN_SIDEBAR_OFFSET_PX = 80;

const CampaignDatePicker = styled(DatePicker)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "#fff",
    transition: theme.transitions.create(["box-shadow", "border-color"], {
      duration: theme.transitions.duration.short,
    }),
    "&:hover": {
      backgroundColor: "#f8fafc",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(37, 99, 235, 0.15)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#2563eb",
        borderWidth: 2,
      },
    },
  },
}));

function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

let razorpayScriptPromise = null;
function ensureRazorpayLoaded() {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
}

function toYMDLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayLocalYMD() {
  return toYMDLocal(new Date());
}

function parseYMD(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return null;
  const [y, m, d] = dateStr.split("-").map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function addDaysYMD(dateStr, days) {
  const dt = parseYMD(dateStr);
  if (!dt) return dateStr;
  dt.setDate(dt.getDate() + days);
  return toYMDLocal(dt);
}

/** Full days between start and end midnights (e.g. Apr 1 → Apr 8 = 7). */
function spanDaysBetween(startStr, endStr) {
  const a = parseYMD(startStr);
  const b = parseYMD(endStr);
  if (!a || !b) return -1;
  return Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
}

function isValidCampaignDateRange(startStr, endStr) {
  const span = spanDaysBetween(startStr, endStr);
  return span >= MIN_CAMPAIGN_SPAN_DAYS;
}

function formatDateDisplay(dateString) {
  if (!dateString) return "N/A";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return dateString;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

/** Inclusive calendar days in the run (start through end). */
function campaignInclusiveDayCount(start, end) {
  const span = spanDaysBetween(start, end);
  if (span < 0) return 0;
  return span + 1;
}

function amountToPaise(amount) {
  return Math.round((Number(amount) || 0) * 100);
}

function paiseToRupees(paise) {
  return (Number(paise) || 0) / 100;
}

function toApiStartAt(dateInput) {
  return `${dateInput} 00:00:00`;
}

function toApiEndAt(dateInput) {
  return `${dateInput} 23:59:59`;
}

function extractNumber(source, keys, fallback = 0) {
  for (const key of keys) {
    if (source?.[key] != null && !Number.isNaN(Number(source[key]))) return Number(source[key]);
  }
  return fallback;
}

function buildFallbackQuote(clicks, cpc) {
  const base = (Number(clicks) || 0) * (Number(cpc) || 0);
  const gst = Math.round(base * (GST_PERCENT / 100));
  const total = base + gst;
  return {
    base_rupees: base,
    gst_rupees: gst,
    total_rupees: total,
    base_paise: amountToPaise(base),
    gst_paise: amountToPaise(gst),
    total_paise: amountToPaise(total),
  };
}

/** Normalize adsQuote API payload to the shape used across the wizard. */
function quoteFromAdsQuoteResponse(d) {
  if (!d || d.status === false) return null;
  const basePaise = extractNumber(d, ["base_amount_paise", "base_paise"], NaN);
  if (Number.isNaN(basePaise)) return null;
  let gstPaise = extractNumber(d, ["gst_amount_paise", "gst_paise"], NaN);
  if (Number.isNaN(gstPaise)) gstPaise = Math.round((basePaise * GST_PERCENT) / 100);
  let payablePaise = extractNumber(
    d,
    ["payable_amount_paise", "payable_paise", "total_amount_paise", "amount_paise"],
    NaN
  );
  if (Number.isNaN(payablePaise)) payablePaise = basePaise + gstPaise;
  return {
    base_paise: basePaise,
    gst_paise: gstPaise,
    total_paise: payablePaise,
    base_rupees: paiseToRupees(basePaise),
    gst_rupees: paiseToRupees(gstPaise),
    total_rupees: paiseToRupees(payablePaise),
  };
}

/** Map campaignCreateOrder / campaignConfirm payload for Payments UI (amounts in paise). */
function normalizeCheckoutBreakdown(data) {
  if (!data || data.status === false) return null;
  let pay = extractNumber(data, ["payable_amount_paise"], NaN);
  const wc = extractNumber(data, ["wallet_credit_applied_paise"], 0);
  const residual = extractNumber(data, ["amount_paise", "razorpay_checkout_amount_paise"], 0);
  if (Number.isNaN(pay)) {
    if (wc > 0 || residual > 0) {
      pay = wc + residual;
    } else {
      return null;
    }
  }
  return {
    payable_amount_paise: pay,
    wallet_credit_applied_paise: wc,
    amount_paise: residual,
    base_amount_paise: data.base_amount_paise != null ? Number(data.base_amount_paise) : null,
    gst_amount_paise: data.gst_amount_paise != null ? Number(data.gst_amount_paise) : null,
  };
}

function parseSavedWizardSession(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Read the first numeric stat present on a wallet property row (API field names vary). */
function pickPropertyStat(row, keys) {
  if (!row) return null;
  for (const k of keys) {
    const v = row[k];
    if (v != null && v !== "" && !Number.isNaN(Number(v))) return Number(v);
  }
  return null;
}

/**
 * Daily rows from `propertyMetrics` when the API returns a breakdown (`daily`, `by_date`, etc.).
 */
function extractMetricsDailySeries(d) {
  if (!d || typeof d !== "object") return [];
  const candidates = [
    d.daily,
    d.daily_breakdown,
    d.by_date,
    d.series,
    d.metrics_timeseries,
    d.timeseries,
    d.rows,
  ];
  if (d.metrics && typeof d.metrics === "object") {
    candidates.push(d.metrics.daily, d.metrics.by_date, d.metrics.series, d.metrics.rows);
  }
  let src = null;
  for (const c of candidates) {
    if (Array.isArray(c) && c.length) {
      src = c;
      break;
    }
  }
  if (!src) return [];
  return src
    .map((row) => ({
      date: String(row.event_date || row.date || row.day || row.metric_date || "").slice(0, 10),
      clicks: Number(row.clicks ?? row.click_count ?? row.clicks_total ?? 0) || 0,
      impressions:
        Number(row.impressions ?? row.impression_count ?? row.impressions_total ?? row.imprs ?? 0) || 0,
    }))
    .filter((row) => row.date);
}

/**
 * `GET CpcAds.php?action=propertyMetrics` — aggregates from `cpc_daily_property_metrics` (see cpcnotes.md).
 * Accepts flat keys or nested `metrics` object.
 */
function normalizePropertyMetricsResponse(d) {
  if (!d || d.status === false) return null;
  const src = d.metrics && typeof d.metrics === "object" ? { ...d, ...d.metrics } : d;
  const impressionsTotal = extractNumber(
    src,
    ["impressions_total", "total_impressions", "impressions", "sum_impressions"],
    NaN
  );
  const clicksTotal = extractNumber(
    src,
    ["clicks_total", "total_clicks", "clicks", "reported_clicks", "sum_clicks"],
    NaN
  );
  const daily = extractMetricsDailySeries(d);
  const out = {};
  if (Number.isFinite(impressionsTotal)) out.impressionsTotal = impressionsTotal;
  if (Number.isFinite(clicksTotal)) out.clicksTotal = clicksTotal;
  if (daily.length) {
    out.daily = daily;
    if (!Number.isFinite(clicksTotal)) out.clicksTotal = daily.reduce((s, x) => s + x.clicks, 0);
    if (!Number.isFinite(impressionsTotal)) out.impressionsTotal = daily.reduce((s, x) => s + x.impressions, 0);
  }
  return Object.keys(out).length ? out : null;
}

const CampaignPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  /** Align with App.js `window.innerWidth < 768` so footer clears the fixed sidebar. */
  const isAppMobile = useMediaQuery("(max-width:767px)");
  const navigate = useNavigate();
  const location = useLocation();
  const { successToast, errorToast } = useCustomToast();

  const normalizedPath = location.pathname.replace(/\/+/g, "/");
  const isRunningCampaignsRoute = /\/campaign\/running\/?$/.test(normalizedPath);
  const isCampaignShellRoute =
    /^\/campaign\/?$/.test(normalizedPath) || /\/campaign\/running\/?$/.test(normalizedPath);

  const token = getDecryptedToken();
  const [cpcIds, setCpcIds] = useState(() => ({
    partnerId: typeof window !== "undefined" ? localStorage.getItem("selectedHostId") || "" : "",
    hotelIdStr: typeof window !== "undefined" ? localStorage.getItem("selectedHotelId") || "" : "",
  }));

  useEffect(() => {
    const readCpcIds = () => {
      setCpcIds({
        partnerId: localStorage.getItem("selectedHostId") || "",
        hotelIdStr: localStorage.getItem("selectedHotelId") || "",
      });
    };
    window.addEventListener("hotelSelected", readCpcIds);
    window.addEventListener("storage", readCpcIds);
    return () => {
      window.removeEventListener("hotelSelected", readCpcIds);
      window.removeEventListener("storage", readCpcIds);
    };
  }, []);

  /** partner_id = host_id (hotel_new.host_id). */
  const { partnerId, hotelIdStr } = cpcIds;
  const hotelId = hotelIdStr ? Number(hotelIdStr) : NaN;
  const hasCpcContext = Boolean(partnerId && hotelIdStr && !Number.isNaN(hotelId));

  const [showBuilder, setShowBuilder] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  /** Running campaigns: optional rollup from action=propertyMetrics (see cpcnotes.md). */
  const [cpcMetrics, setCpcMetrics] = useState(null);
  const [cpcMetricsLoading, setCpcMetricsLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [startLoading, setStartLoading] = useState(false);
  const [pauseLoading, setPauseLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [clicks, setClicks] = useState(DEFAULT_CLICKS);
  const [cpc, setCpc] = useState(DEFAULT_CPC);
  const [campaignName, setCampaignName] = useState("");
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  /** When true, ask server to apply wallet (see backend: apply_existing_wallet / wallet credit cap). */
  const [useWalletForCampaign, setUseWalletForCampaign] = useState(true);
  const [quoteSummary, setQuoteSummary] = useState(() => buildFallbackQuote(DEFAULT_CLICKS, DEFAULT_CPC));
  const [quoteLoading, setQuoteLoading] = useState(false);
  /** After checkout — authoritative payable, wallet credit, Razorpay residual (paise). */
  const [serverCheckoutBreakdown, setServerCheckoutBreakdown] = useState(null);

  /** After pay, move to Launch once; going Back to Payments keeps ref true so we do not yank forward again. */
  const prevPaymentConfirmedRef = useRef(false);
  useEffect(() => {
    if (showBuilder && activeStep === 1 && paymentConfirmed && !prevPaymentConfirmedRef.current) {
      setActiveStep(2);
    }
    prevPaymentConfirmedRef.current = paymentConfirmed;
  }, [showBuilder, activeStep, paymentConfirmed]);

  /** After session restore, skip one payment-reset that matches these params (restored state triggers dep change). */
  const skipPaymentResetForKeyRef = useRef(null);
  const prevWizardCtxRef = useRef(null);

  const resetWizardDraft = useCallback(() => {
    setActiveStep(0);
    setStartDate("");
    setEndDate("");
    setClicks(DEFAULT_CLICKS);
    setCpc(DEFAULT_CPC);
    setCampaignName("");
    setPaymentConfirmed(false);
    setTermsAccepted(false);
    setUseWalletForCampaign(true);
    setServerCheckoutBreakdown(null);
    setQuoteSummary(buildFallbackQuote(DEFAULT_CLICKS, DEFAULT_CPC));
  }, []);

  /** Switching property mid-session must not keep another hotel's wizard payload. */
  useEffect(() => {
    if (!hasCpcContext) {
      prevWizardCtxRef.current = null;
      return;
    }
    const ctx = `${partnerId}|${hotelIdStr}`;
    const prev = prevWizardCtxRef.current;
    prevWizardCtxRef.current = ctx;
    if (prev == null) return;
    if (prev === ctx) return;
    try {
      sessionStorage.removeItem(CPC_WIZARD_SESSION_KEY);
    } catch {
      /* ignore */
    }
    setShowBuilder(false);
    resetWizardDraft();
  }, [hasCpcContext, partnerId, hotelIdStr, resetWizardDraft]);

  /**
   * Restore wizard after refresh (sessionStorage, same tab only).
   * Step 0: stay in builder but clear draft (selections not persisted on details-only).
   * Step ≥ 1: restore payment/launch draft so refresh on Payments or Launch keeps progress.
   */
  useLayoutEffect(() => {
    if (!hasCpcContext) return;
    let raw = null;
    try {
      raw = sessionStorage.getItem(CPC_WIZARD_SESSION_KEY);
    } catch {
      return;
    }
    const snap = parseSavedWizardSession(raw);
    if (!snap || snap.v !== 1) {
      if (snap && snap.v !== 1) {
        try {
          sessionStorage.removeItem(CPC_WIZARD_SESSION_KEY);
        } catch {
          /* ignore */
        }
      }
      return;
    }
    if (snap.partnerId !== partnerId || snap.hotelIdStr !== hotelIdStr) {
      try {
        sessionStorage.removeItem(CPC_WIZARD_SESSION_KEY);
      } catch {
        /* ignore */
      }
      return;
    }
    if (!snap.showBuilder) return;

    setShowBuilder(true);
    const step = Math.min(2, Math.max(0, Number(snap.activeStep) || 0));
    setActiveStep(step);

    if (step === 0) {
      setStartDate("");
      setEndDate("");
      setClicks(DEFAULT_CLICKS);
      setCpc(DEFAULT_CPC);
      setCampaignName("");
      setPaymentConfirmed(false);
      setTermsAccepted(false);
      setUseWalletForCampaign(true);
      setServerCheckoutBreakdown(null);
      setQuoteSummary(buildFallbackQuote(DEFAULT_CLICKS, DEFAULT_CPC));
      skipPaymentResetForKeyRef.current = JSON.stringify({
        clicks: DEFAULT_CLICKS,
        cpc: DEFAULT_CPC,
        startDate: "",
        endDate: "",
        useWalletForCampaign: true,
      });
    } else {
      const cpcClamped = Math.max(MIN_CPC, Math.min(MAX_CPC, Number(snap.cpc) || DEFAULT_CPC));
      const clicksClamped = Math.min(MAX_CLICKS, Math.max(MIN_CLICKS, Number(snap.clicks) || DEFAULT_CLICKS));
      const nextStart = typeof snap.startDate === "string" ? snap.startDate : "";
      const nextEnd = typeof snap.endDate === "string" ? snap.endDate : "";
      const nextWallet = snap.useWalletForCampaign !== false;
      setStartDate(nextStart);
      setEndDate(nextEnd);
      setClicks(clicksClamped);
      setCpc(cpcClamped);
      setCampaignName(typeof snap.campaignName === "string" ? snap.campaignName : "");
      setPaymentConfirmed(Boolean(snap.paymentConfirmed));
      setTermsAccepted(Boolean(snap.termsAccepted));
      setUseWalletForCampaign(nextWallet);
      setServerCheckoutBreakdown(
        snap.serverCheckoutBreakdown && typeof snap.serverCheckoutBreakdown === "object"
          ? snap.serverCheckoutBreakdown
          : null
      );
      if (snap.quoteSummary && typeof snap.quoteSummary === "object") {
        setQuoteSummary(snap.quoteSummary);
      } else {
        setQuoteSummary(buildFallbackQuote(clicksClamped, cpcClamped));
      }
      skipPaymentResetForKeyRef.current = JSON.stringify({
        clicks: clicksClamped,
        cpc: cpcClamped,
        startDate: nextStart,
        endDate: nextEnd,
        useWalletForCampaign: nextWallet,
      });
    }

    window.requestAnimationFrame(() => {
      document.getElementById("campaign-builder")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [hasCpcContext, partnerId, hotelIdStr]);

  /** Persist open wizard; full draft only from Payments onward (step ≥ 1). */
  useEffect(() => {
    if (typeof sessionStorage === "undefined" || !hasCpcContext || !showBuilder) return;
    const base = {
      v: 1,
      partnerId,
      hotelIdStr,
      showBuilder: true,
      activeStep,
    };
    const payload =
      activeStep >= 1
        ? {
            ...base,
            startDate,
            endDate,
            clicks,
            cpc,
            campaignName,
            paymentConfirmed,
            termsAccepted,
            useWalletForCampaign,
            serverCheckoutBreakdown,
            quoteSummary,
          }
        : base;
    try {
      sessionStorage.setItem(CPC_WIZARD_SESSION_KEY, JSON.stringify(payload));
    } catch {
      /* ignore */
    }
  }, [
    hasCpcContext,
    showBuilder,
    activeStep,
    partnerId,
    hotelIdStr,
    startDate,
    endDate,
    clicks,
    cpc,
    campaignName,
    paymentConfirmed,
    termsAccepted,
    useWalletForCampaign,
    serverCheckoutBreakdown,
    quoteSummary,
  ]);

  const properties = useMemo(
    () => (Array.isArray(wallet?.properties) ? wallet.properties : []),
    [wallet?.properties]
  );
  const activeProperty = useMemo(() => {
    if (!properties.length) return null;
    return properties.find((p) => Number(p.hotel_id) === hotelId) || properties[0];
  }, [properties, hotelId]);

  /** cpc_properties PK — never substitute hotel_id. */
  const internalCpcPropertyId = useMemo(() => {
    const raw = activeProperty?.property_id;
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [activeProperty?.property_id]);

  /**
   * Optional property_id for GETs must NOT live in fetchWallet/campaignSync deps — it appears only after the first
   * wallet response, which was causing a second full sync+wallet round-trip on every mount.
   */
  const internalCpcPropertyIdRef = useRef(null);
  useEffect(() => {
    const rowHotel =
      activeProperty?.hotel_id != null ? Number(activeProperty.hotel_id) : null;
    internalCpcPropertyIdRef.current =
      rowHotel === hotelId ? internalCpcPropertyId : null;
  }, [internalCpcPropertyId, activeProperty?.hotel_id, hotelId]);

  const fetchWallet = useCallback(async () => {
    if (!token || !partnerId || !hasCpcContext) {
      setWalletLoading(false);
      return;
    }
    setWalletLoading(true);
    try {
      const params = {
        action: "getWallet",
        partner_id: partnerId,
        hotel_id: hotelId,
        tx_limit: 50,
      };
      const pid = internalCpcPropertyIdRef.current;
      if (pid != null) {
        params.property_id = pid;
      }
      const res = await axios.get(CPC_ADS_BASE, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data && res.data.status !== false) {
        setWallet(res.data);
      } else {
        setWallet({ properties: [], wallet_balance_paise: 0, currency: "INR" });
        errorToast(res.data?.message || "Unable to load wallet for this property.");
      }
    } catch (err) {
      console.error("getWallet error:", err);
      setWallet({ properties: [], wallet_balance_paise: 0, currency: "INR" });
      errorToast(err.response?.data?.message || "Unable to load campaign wallet data.");
    } finally {
      setWalletLoading(false);
    }
  }, [token, partnerId, hotelId, hasCpcContext, errorToast]);

  /** Rollups from cpc_daily_property_metrics — requires PHP action `propertyMetrics` (cpcnotes.md). */
  const fetchCpcMetrics = useCallback(async () => {
    if (!token || !partnerId || !hasCpcContext) return;
    setCpcMetricsLoading(true);
    try {
      const params = {
        action: "propertyMetrics",
        partner_id: partnerId,
        hotel_id: hotelId,
      };
      const pid = internalCpcPropertyIdRef.current;
      if (pid != null) params.property_id = pid;
      const res = await axios.get(CPC_ADS_BASE, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setCpcMetrics(normalizePropertyMetricsResponse(res.data));
    } catch {
      setCpcMetrics(null);
    } finally {
      setCpcMetricsLoading(false);
    }
  }, [token, partnerId, hotelId, hasCpcContext]);

  const campaignSync = useCallback(async () => {
    if (!token || !partnerId || !hasCpcContext) return;
    try {
      const syncParams = { action: "campaignSync", partner_id: partnerId, hotel_id: hotelId };
      const pid = internalCpcPropertyIdRef.current;
      if (pid != null) {
        syncParams.property_id = pid;
      }
      const res = await axios.get(CPC_ADS_BASE, {
        params: syncParams,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.status === false && res.data?.message) {
        console.warn("[campaignSync]", res.data.message);
      }
    } catch (err) {
      console.warn("[campaignSync]", err.response?.data?.message || err.message);
    }
  }, [token, partnerId, hotelId, hasCpcContext]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await campaignSync();
      if (!cancelled) await fetchWallet();
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignSync, fetchWallet]);

  useEffect(() => {
    if (!hasCpcContext || !isCampaignShellRoute) return;
    fetchCpcMetrics();
  }, [hasCpcContext, isCampaignShellRoute, fetchCpcMetrics, internalCpcPropertyId]);

  const errorToastRef = useRef(errorToast);
  errorToastRef.current = errorToast;

  const fetchAdsQuote = useCallback(
    async (signal) => {
      if (!token || !hasCpcContext) return;
      setQuoteLoading(true);
      try {
        const res = await axios.post(
          CPC_ADS_BASE,
          { clicks: Number(clicks), cpc_rate_rupees: Number(cpc) },
          {
            params: { action: "adsQuote" },
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            signal,
          }
        );
        if (res.data?.status === false) {
          errorToastRef.current(res.data?.message || "Unable to load campaign quote.");
          setQuoteSummary(buildFallbackQuote(clicks, cpc));
          return;
        }
        const next = quoteFromAdsQuoteResponse(res.data);
        if (next) setQuoteSummary(next);
        else setQuoteSummary(buildFallbackQuote(clicks, cpc));
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        errorToastRef.current(err.response?.data?.message || "Unable to load campaign quote.");
        setQuoteSummary(buildFallbackQuote(clicks, cpc));
      } finally {
        if (!signal?.aborted) setQuoteLoading(false);
      }
    },
    [token, clicks, cpc, hasCpcContext]
  );

  /** adsQuote only after user opens the builder — not on marketing landing (avoids useless API + double calls with wallet load). */
  useEffect(() => {
    if (!showBuilder || !hasCpcContext) return;
    const ac = new AbortController();
    fetchAdsQuote(ac.signal);
    return () => ac.abort();
  }, [showBuilder, hasCpcContext, fetchAdsQuote]);

  useEffect(() => {
    setCpc((c) => Math.max(MIN_CPC, Math.min(MAX_CPC, Number(c) || MIN_CPC)));
  }, []);

  useEffect(() => {
    setClicks((c) => Math.min(MAX_CLICKS, Math.max(MIN_CLICKS, Number(c) || MIN_CLICKS)));
  }, []);

  useEffect(() => {
    const key = JSON.stringify({
      clicks,
      cpc,
      startDate,
      endDate,
      useWalletForCampaign,
    });
    if (skipPaymentResetForKeyRef.current === key) {
      skipPaymentResetForKeyRef.current = null;
      return;
    }
    setPaymentConfirmed(false);
    setServerCheckoutBreakdown(null);
  }, [clicks, cpc, startDate, endDate, useWalletForCampaign]);

  useEffect(() => {
    if (showBuilder && activeStep === 2 && paymentConfirmed && hasCpcContext) {
      fetchWallet();
    }
  }, [showBuilder, activeStep, paymentConfirmed, hasCpcContext, fetchWallet]);

  const minEndDateStr = useMemo(() => {
    if (!startDate) return "";
    return addDaysYMD(startDate, MIN_CAMPAIGN_SPAN_DAYS);
  }, [startDate]);
  const inclusiveDayCount = useMemo(
    () => campaignInclusiveDayCount(startDate, endDate),
    [startDate, endDate]
  );
  const dateRangeValid = useMemo(() => {
    if (!startDate || !endDate) return false;
    if (startDate < todayLocalYMD()) return false;
    return isValidCampaignDateRange(startDate, endDate);
  }, [startDate, endDate]);

  const todayAsDate = parseYMD(todayLocalYMD());
  const startDateObj = startDate ? parseYMD(startDate) : null;
  const endDateObj = endDate ? parseYMD(endDate) : null;
  const minEndDateObj = minEndDateStr ? parseYMD(minEndDateStr) : null;

  const currency = wallet?.currency || "INR";

  const walletBalancePaise = extractNumber(
    activeProperty || wallet,
    ["wallet_balance_paise", "balance_paise", "wallet_paise"],
    extractNumber(wallet, ["wallet_balance_paise"], 0)
  );

  /** Funds earmarked for the active prepaid pack (`run_budget − run_spent`). */
  const campaignFundsLockedPaise = useMemo(
    () =>
      Math.max(
        0,
        extractNumber(
          activeProperty || {},
          ["campaign_funds_locked_paise", "campaign_budget_remaining_paise"],
          0
        )
      ),
    [activeProperty]
  );

  /**
   * Ledger may include locked pack value; checkout may only use spendable (see cpc-prepaid-campaign-flow.md).
   */
  const walletSpendablePaise = useMemo(() => {
    const ledger = walletBalancePaise;
    const s = extractNumber(activeProperty || {}, ["wallet_spendable_paise"], NaN);
    if (Number.isFinite(s) && s >= 0) return Math.min(s, ledger);
    return ledger;
  }, [activeProperty, walletBalancePaise]);

  const campaignStatus = activeProperty?.ads_enabled ? "Running" : "Stopped";

  /**
   * After adsPause, host can call adsStart with resume:true (keeps run_spent, dates, prepaid counters).
   * Hide when period ended, pack clicks exhausted, or run budget already spent.
   */
  const canResumePausedCampaign = useMemo(() => {
    if (!activeProperty || activeProperty.ads_enabled) return false;
    const endRaw = activeProperty.campaign_end_at || activeProperty.end_at;
    if (endRaw) {
      const t = new Date(endRaw).getTime();
      if (!Number.isNaN(t) && t < Date.now()) return false;
    }
    const packTotal = pickPropertyStat(activeProperty, ["prepaid_clicks_total"], null);
    const packConsumed = pickPropertyStat(activeProperty, ["prepaid_clicks_consumed"], null);
    if (packTotal != null && packTotal > 0 && packConsumed != null && packConsumed >= packTotal) return false;
    const runBudget = extractNumber(activeProperty, ["run_budget_paise"], 0);
    const runSpent = extractNumber(activeProperty, ["run_spent_paise"], 0);
    if (runBudget > 0 && runSpent >= runBudget) return false;
    const hasPackSignals =
      (packTotal != null && packTotal > 0) ||
      runBudget > 0 ||
      Boolean((activeProperty.campaign_name && String(activeProperty.campaign_name).trim()) || "") ||
      Boolean(
        activeProperty.campaign_end_at ||
          activeProperty.campaign_started_at ||
          activeProperty.campaign_start_at ||
          activeProperty.start_at
      );
    return hasPackSignals;
  }, [activeProperty]);

  /** Credit from wallet toward this campaign package: capped at payable (incl. GST) and spendable balance. */
  const walletDeductionPaise = useMemo(() => {
    if (!useWalletForCampaign) return 0;
    const w = walletSpendablePaise;
    if (!w || w <= 0) return 0;
    return Math.min(w, quoteSummary.total_paise);
  }, [useWalletForCampaign, walletSpendablePaise, quoteSummary.total_paise]);

  useEffect(() => {
    if (walletSpendablePaise <= 0 && useWalletForCampaign) {
      setUseWalletForCampaign(false);
    }
  }, [walletSpendablePaise, useWalletForCampaign]);

  const amountDuePaise = useMemo(
    () => Math.max(0, quoteSummary.total_paise - walletDeductionPaise),
    [quoteSummary.total_paise, walletDeductionPaise]
  );

  /** Prefer server fields after create/confirm; else adsQuote + client wallet estimate. */
  const paymentLineItems = useMemo(() => {
    const srv = serverCheckoutBreakdown;
    if (srv && Number.isFinite(Number(srv.payable_amount_paise))) {
      const pay = Number(srv.payable_amount_paise);
      const wc = Number(srv.wallet_credit_applied_paise ?? 0);
      const res = Number(srv.amount_paise ?? Math.max(0, pay - wc));
      const baseRupees =
        srv.base_amount_paise != null && Number.isFinite(Number(srv.base_amount_paise))
          ? paiseToRupees(Number(srv.base_amount_paise))
          : quoteSummary.base_rupees;
      const gstRupees =
        srv.gst_amount_paise != null && Number.isFinite(Number(srv.gst_amount_paise))
          ? paiseToRupees(Number(srv.gst_amount_paise))
          : quoteSummary.gst_rupees;
    return {
        fromServer: true,
        payPaise: pay,
        walletCreditPaise: wc,
        residualPaise: res,
        baseRupees,
        gstRupees,
      };
    }
    return {
      fromServer: false,
      payPaise: quoteSummary.total_paise,
      walletCreditPaise: walletDeductionPaise,
      residualPaise: amountDuePaise,
      baseRupees: quoteSummary.base_rupees,
      gstRupees: quoteSummary.gst_rupees,
    };
  }, [serverCheckoutBreakdown, quoteSummary, walletDeductionPaise, amountDuePaise]);

  const runningStatClicks = useMemo(
    () =>
      cpcMetrics?.clicksTotal ??
      pickPropertyStat(activeProperty, [
        "prepaid_clicks_consumed",
        "daily_clicks_total",
        "metrics_clicks_total",
        "clicks_delivered",
        "clicks_consumed",
        "clicks_used",
        "consumed_clicks",
        "total_clicks_delivered",
        "ad_clicks",
        "stats_clicks",
        "click_count",
      ]),
    [cpcMetrics?.clicksTotal, activeProperty]
  );

  const runningStatImpressions = useMemo(
    () =>
      cpcMetrics?.impressionsTotal ??
      pickPropertyStat(activeProperty, [
        "daily_impressions_total",
        "metrics_impressions_total",
        "impressions",
        "impression_count",
        "total_impressions",
        "ads_impressions",
        "stats_impressions",
      ]),
    [cpcMetrics?.impressionsTotal, activeProperty]
  );

  const overviewPackConsumed = pickPropertyStat(activeProperty, ["prepaid_clicks_consumed"], null);
  const overviewPackTotal = pickPropertyStat(activeProperty, ["prepaid_clicks_total"], null);

  const campaignMetricsChartConfig = useMemo(() => {
    if (!hasCpcContext) return null;
    const daily = Array.isArray(cpcMetrics?.daily) ? cpcMetrics.daily : [];
    const clicks = runningStatClicks ?? 0;
    const imps = runningStatImpressions ?? 0;

    if (daily.length >= 2) {
      return {
        chartKind: "line",
        data: {
          labels: daily.map((x) => (x.date.length >= 10 ? x.date.slice(5) : x.date)),
          datasets: [
            {
              label: "Clicks",
              data: daily.map((x) => x.clicks),
              borderColor: "#2563eb",
              backgroundColor: "rgba(37, 99, 235, 0.12)",
              fill: true,
              tension: 0.35,
              yAxisID: "y",
            },
            {
              label: "Impressions",
              data: daily.map((x) => x.impressions),
              borderColor: "#ea580c",
              backgroundColor: "rgba(234, 88, 12, 0.1)",
              fill: true,
              tension: 0.35,
              yAxisID: "y1",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: { position: "top" },
          },
          scales: {
            y: {
              type: "linear",
              display: true,
              position: "left",
              title: { display: true, text: "Clicks" },
              beginAtZero: true,
              grid: { color: "rgba(148, 163, 184, 0.25)" },
            },
            y1: {
              type: "linear",
              display: true,
              position: "right",
              title: { display: true, text: "Impressions" },
              beginAtZero: true,
              grid: { drawOnChartArea: false },
            },
          },
        },
      };
    }

    return {
      chartKind: "bar",
      data: {
        labels: ["Clicks", "Impressions"],
        datasets: [
          {
            label: "Period total",
            data: [Number(clicks) || 0, Number(imps) || 0],
            backgroundColor: ["rgba(37, 99, 235, 0.88)", "rgba(234, 88, 12, 0.88)"],
            borderRadius: 10,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          y: { beginAtZero: true, ticks: { precision: 0 } },
        },
      },
    };
  }, [hasCpcContext, cpcMetrics?.daily, runningStatClicks, runningStatImpressions]);

  const runBudgetPaise = quoteSummary.base_paise || amountToPaise(clicks * cpc);

  const formatMoneyPaise = (paise) =>
    `${currency} ${paiseToRupees(paise).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const handleCreateOrderAndPay = async () => {
    if (!token || !partnerId || !hasCpcContext) return;
    const totalPaise = quoteSummary.total_paise;
    if (!totalPaise || totalPaise <= 0) {
      errorToast("Enter a valid clicks and CPC amount.");
      return;
    }
    if (!dateRangeValid) {
      errorToast(`Fix your campaign dates (minimum ${MIN_CAMPAIGN_SPAN_DAYS} days) before paying.`);
      return;
    }
    if (!campaignNameValid) {
      errorToast("Enter a campaign name before paying.");
      return;
    }

    setPaymentLoading(true);
    try {
      const creditCap = useWalletForCampaign ? Math.min(walletSpendablePaise, quoteSummary.total_paise) : 0;
      const createBody = {
          partner_id: partnerId,
        hotel_id: hotelId,
        campaign_name: campaignName.trim(),
        target_clicks: Number(clicks),
        clicks: Number(clicks),
        cpc_rate_rupees: Number(cpc),
        campaign_start_at: startDate,
        campaign_end_at: endDate,
        start_at: toApiStartAt(startDate),
        end_at: toApiEndAt(endDate),
        apply_existing_wallet: useWalletForCampaign,
        max_wallet_credit_paise: creditCap,
      };
      if (internalCpcPropertyId != null) {
        createBody.property_id = internalCpcPropertyId;
      }

      const orderRes = await axios.post(CPC_ADS_BASE, createBody, {
        params: { action: "campaignCreateOrder" },
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      const data = orderRes.data;
      if (data && data.status === false) {
        errorToast(data.message || "Unable to create payment order.");
        setPaymentLoading(false);
        return;
      }

      const breakdown = normalizeCheckoutBreakdown(data);
      if (breakdown) {
        setServerCheckoutBreakdown(breakdown);
      }

      const rzOrderId = data.order_id || data.razorpay_order_id;
      const rzKey = data.key_id;
      const residualPaise = Number(data.amount_paise ?? 0);
      const checkoutSkipped =
        data.checkout_skipped === true ||
        data.checkout_skipped === 1 ||
        data.checkout_skipped === "true" ||
        data.checkout_skipped === "1";

      if (!rzOrderId && !rzKey) {
        if (
          checkoutSkipped &&
          data?.status === true &&
          residualPaise <= 0
        ) {
          setPaymentConfirmed(true);
          successToast(data?.message || "Payment complete. Your wallet balance will update in a moment.");
          await fetchWallet();
        } else {
          errorToast(
            data?.message ||
              "Could not start checkout. Please try again or contact support if this keeps happening."
          );
        }
        setPaymentLoading(false);
        return;
      }

      if (!residualPaise || residualPaise <= 0) {
        errorToast(data?.message || "We could not start the payment — the amount from the server was invalid.");
        setPaymentLoading(false);
        return;
      }

      const razorpayReady = await ensureRazorpayLoaded();
      if (!razorpayReady || typeof window.Razorpay === "undefined") {
        errorToast("Razorpay not available in browser.");
        setPaymentLoading(false);
        return;
      }

      // Always prefer backend-provided key_id for this order.
      const razorpayKeyForCheckout = rzKey || CPC_CHECKOUT_KEY_ID;
      if (!razorpayKeyForCheckout) {
        errorToast("Razorpay key missing for checkout.");
        setPaymentLoading(false);
        return;
      }
      if (
        CPC_CHECKOUT_KEY_ID &&
        rzKey &&
        CPC_CHECKOUT_KEY_ID !== rzKey &&
        process.env.NODE_ENV === "development"
      ) {
        console.warn(
          "[CPC] Backend returned a different Razorpay key_id than REACT_APP_RAZORPAY_KEY_ID. Using backend key_id for this checkout."
        );
      }

      const rzp = new window.Razorpay({
        // Keep checkout payload minimal when using server-created order_id.
        key: razorpayKeyForCheckout,
        order_id: rzOrderId,
        name: "TabTrips",
        description: campaignName?.trim() ? `Campaign: ${campaignName.trim()}` : "Sponsored Listing Campaign",
        theme: { color: "#2563eb" },
        handler: async (response) => {
              try {
                const confirmRes = await axios.post(
                  CPC_ADS_BASE,
                  {
                    razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                    razorpay_signature: response.razorpay_signature,
                  },
              {
                params: { action: "campaignConfirm" },
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            if (confirmRes.data?.status === true || confirmRes.data?.already_completed) {
              const postPay = normalizeCheckoutBreakdown(confirmRes.data);
              if (postPay) setServerCheckoutBreakdown(postPay);
              setPaymentConfirmed(true);
              successToast("Payment successful.");
                  fetchWallet();
                } else {
              errorToast(confirmRes.data?.message || "Payment confirm failed.");
            }
          } catch (err) {
            errorToast(err.response?.data?.message || "Payment confirm failed.");
          } finally {
            setPaymentLoading(false);
          }
        },
      });

      rzp.on("payment.failed", (resp) => {
        setPaymentLoading(false);
        const msg =
          resp?.error?.description ||
          resp?.error?.reason ||
          `Razorpay checkout failed for order ${rzOrderId}.`;
        errorToast(msg);
          });
          rzp.open();
    } catch (err) {
      setPaymentLoading(false);
      errorToast(err.response?.data?.message || "Order creation failed.");
    }
  };

  const handleStartCampaign = async () => {
    if (!startDate || !endDate) {
      errorToast("Choose campaign start and end dates.");
      return;
    }
    if (!dateRangeValid) {
      errorToast(`Campaign must run at least ${MIN_CAMPAIGN_SPAN_DAYS} days. End date must be ${MIN_CAMPAIGN_SPAN_DAYS} or more days after start, and start cannot be in the past.`);
      return;
    }
    if (!paymentConfirmed) {
      errorToast("Complete payment first.");
      return;
    }
    if (!campaignNameValid) {
      errorToast("Enter a campaign name before launching.");
      return;
    }
    if (!termsAccepted) {
      errorToast("Please accept the terms to launch.");
      return;
    }

    setStartLoading(true);
    try {
      const adsStartBody = {
        hotel_id: hotelId,
        start_at: toApiStartAt(startDate),
        end_at: toApiEndAt(endDate),
        run_budget_paise: runBudgetPaise,
        cpc_rate_rupees: Number(cpc),
      };
      if (internalCpcPropertyId != null) {
        adsStartBody.property_id = internalCpcPropertyId;
      }
      const res = await axios.post(CPC_ADS_BASE, adsStartBody, {
        params: { action: "adsStart" },
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (res.data && res.data.status !== false) {
        successToast("Campaign started successfully.");
        try {
          sessionStorage.removeItem(CPC_WIZARD_SESSION_KEY);
        } catch {
          /* ignore */
        }
        setShowBuilder(false);
        resetWizardDraft();
        fetchWallet();
        navigate("/campaign/running");
      } else {
        errorToast(res.data?.message || "Unable to start campaign.");
      }
    } catch (err) {
      errorToast(err.response?.data?.message || "Unable to start campaign.");
    } finally {
      setStartLoading(false);
    }
  };

  const handlePauseCampaign = async () => {
    setPauseLoading(true);
    try {
      const adsPauseBody = { hotel_id: hotelId };
      if (internalCpcPropertyId != null) {
        adsPauseBody.property_id = internalCpcPropertyId;
      }
      const res = await axios.post(CPC_ADS_BASE, adsPauseBody, {
        params: { action: "adsPause" },
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (res.data && res.data.status !== false) {
        successToast("Campaign paused.");
        fetchWallet();
      } else {
        errorToast(res.data?.message || "Unable to pause campaign.");
      }
    } catch (err) {
      errorToast(err.response?.data?.message || "Unable to pause campaign.");
    } finally {
      setPauseLoading(false);
    }
  };

  /** Resume after pause — POST adsStart with resume:true (does not reset run_spent / pack). */
  const handleResumeCampaign = async () => {
    if (!token || !hasCpcContext) return;
    setResumeLoading(true);
    try {
      const body = { hotel_id: hotelId, resume: true };
      if (internalCpcPropertyId != null) {
        body.property_id = internalCpcPropertyId;
      }
      const res = await axios.post(CPC_ADS_BASE, body, {
        params: { action: "adsStart" },
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (res.data && res.data.status !== false) {
        successToast(res.data?.message || "Campaign resumed.");
        fetchWallet();
        if (isRunningCampaignsRoute) {
          fetchCpcMetrics();
        }
      } else {
        errorToast(res.data?.message || "Unable to resume campaign.");
      }
    } catch (err) {
      errorToast(err.response?.data?.message || "Unable to resume campaign.");
    } finally {
      setResumeLoading(false);
    }
  };

  const openBuilder = () => {
    setShowBuilder(true);
    setActiveStep(0);
    window.requestAnimationFrame(() => {
      document.getElementById("campaign-builder")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const closeBuilder = () => {
    try {
      sessionStorage.removeItem(CPC_WIZARD_SESSION_KEY);
    } catch {
      /* ignore */
    }
    setShowBuilder(false);
    resetWizardDraft();
  };

  const clicksValid = Number(clicks) >= MIN_CLICKS && Number(clicks) <= MAX_CLICKS;
  const campaignNameValid = campaignName.trim().length > 0;
  const showCampaignNameRequired =
    dateRangeValid && clicksValid && Number(cpc) >= MIN_CPC && Number(cpc) <= MAX_CPC && !campaignNameValid;
  const canGoStep2 =
    dateRangeValid &&
    clicksValid &&
    Number(cpc) >= MIN_CPC &&
    Number(cpc) <= MAX_CPC &&
    campaignNameValid;
  const baseCostLabel = `${Number(clicks).toLocaleString()} clicks × ${currency} ${Number(cpc).toLocaleString()}`;

  if (!token) {
    return (
      <Box sx={{ p: 3, maxWidth: 460, mx: "auto" }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Please log in to create sponsored listing campaigns.
        </Alert>
        <Button variant="contained" onClick={() => navigate("/")}>
          Go to dashboard
        </Button>
      </Box>
    );
  }

  if (!hasCpcContext) {
  return (
      <Box sx={{ p: 3, maxWidth: 520, mx: "auto" }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Choose a property from the selector at the top of the page or from My Properties to manage sponsored listings
          and wallet for that listing.
        </Alert>
        <Button variant="contained" onClick={() => navigate("/")}>
          Go to dashboard
        </Button>
        </Box>
    );
  }

  if (isRunningCampaignsRoute) {
    const statClicks = runningStatClicks;
    const statImpressions = runningStatImpressions;
    const packConsumed = pickPropertyStat(activeProperty, ["prepaid_clicks_consumed"], null);
    const packTotal = pickPropertyStat(activeProperty, ["prepaid_clicks_total"], null);
    const fmtInt = (n) => (n == null ? "—" : Number(n).toLocaleString());
    const currentCampaignName = (activeProperty?.campaign_name && String(activeProperty.campaign_name).trim()) || "";
    const campaignPeriodStart =
      activeProperty?.campaign_started_at || activeProperty?.campaign_start_at || activeProperty?.start_at;
    const campaignPeriodEnd = activeProperty?.campaign_end_at || activeProperty?.end_at;
    const packCpcRatePaise = extractNumber(activeProperty || {}, ["cpc_rate_per_click_paise"], NaN);
    const packRunBudgetPaise = extractNumber(activeProperty || {}, ["run_budget_paise"], NaN);
    const packRunSpentPaise = extractNumber(activeProperty || {}, ["run_spent_paise"], NaN);
    const showCurrentPackCard =
      (packTotal != null && packTotal > 0) ||
      Boolean(currentCampaignName) ||
      Boolean(campaignPeriodStart || campaignPeriodEnd) ||
      (Number.isFinite(packRunBudgetPaise) && packRunBudgetPaise > 0);

    return (
      <Box
        sx={{
          minHeight: "100%",
          bgcolor: "#eef4fb",
          backgroundImage:
            "radial-gradient(circle at 2px 2px, rgba(37, 99, 235, 0.06) 1px, transparent 0)",
          backgroundSize: "28px 28px",
          pb: 4,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 3 }, pt: { xs: 3, md: 4 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ sm: "center" }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h4" fontWeight={800} sx={{ color: "#0f172a", letterSpacing: "-0.02em" }}>
                Running campaigns
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5, maxWidth: 640 }}>
                Performance and spend for your active sponsored listing. Clicks and impressions show visibility; the
                numbers under your package show how many paid clicks have been used.
        </Typography>
      </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {campaignStatus === "Running" && (
                <Button
                  variant="outlined"
                  color="warning"
                  disabled={pauseLoading || resumeLoading}
                  onClick={handlePauseCampaign}
                  startIcon={pauseLoading ? <CircularProgress size={18} color="inherit" /> : <PauseIcon />}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
                >
                  {pauseLoading ? "Pausing…" : "Pause"}
                </Button>
              )}
              {canResumePausedCampaign && (
                <Button
                  variant="contained"
                  color="success"
                  disabled={resumeLoading || pauseLoading || walletLoading}
                  onClick={handleResumeCampaign}
                  startIcon={resumeLoading ? <CircularProgress size={18} color="inherit" /> : <PlayIcon />}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
                >
                  {resumeLoading ? "Resuming…" : "Resume"}
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() => {
                  fetchWallet();
                  fetchCpcMetrics();
                }}
                disabled={walletLoading}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate("/campaign")}
          sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 2,
                  background: "linear-gradient(90deg, #f97316 0%, #ea580c 100%)",
                  "&:hover": { background: "linear-gradient(90deg, #ea580c 0%, #c2410c 100%)" },
                }}
              >
                Create campaign
              </Button>
            </Stack>
          </Stack>

          {walletLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: "#2563eb" }} />
          </Box>
        ) : (
          <>
              {showCurrentPackCard && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 2,
                    borderRadius: 3,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#fff",
                    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ sm: "flex-start" }}
                    spacing={2}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.06}>
                        Current campaign
                      </Typography>
                      <Typography variant="h6" fontWeight={800} sx={{ mt: 0.5, color: "#0f172a" }}>
                        {currentCampaignName || "Sponsored listing"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 560 }}>
                        {formatDateDisplay(campaignPeriodStart)} → {formatDateDisplay(campaignPeriodEnd)}
                      </Typography>
                    </Box>
                    <Stack spacing={0.75} sx={{ minWidth: { sm: 260 } }}>
                      {Number.isFinite(packCpcRatePaise) && packCpcRatePaise > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          <strong style={{ color: "#0f172a" }}>Cost per click: </strong>
                          {formatMoneyPaise(packCpcRatePaise)}
                        </Typography>
                      )}
                      {Number.isFinite(packRunBudgetPaise) && packRunBudgetPaise > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          <strong style={{ color: "#0f172a" }}>Click package budget: </strong>
                          {formatMoneyPaise(packRunBudgetPaise)}
                        </Typography>
                      )}
                      {Number.isFinite(packRunSpentPaise) && packRunSpentPaise >= 0 && Number.isFinite(packRunBudgetPaise) && (
                        <Typography variant="body2" color="text.secondary">
                          <strong style={{ color: "#0f172a" }}>Used from this package: </strong>
                          {formatMoneyPaise(packRunSpentPaise)}
                          {packRunBudgetPaise > 0 && (
                            <span>
                              {" "}
                              · {formatMoneyPaise(Math.max(0, packRunBudgetPaise - packRunSpentPaise))} remaining
                            </span>
                          )}
                        </Typography>
                      )}
                      {packConsumed != null && packTotal != null && (
                        <Typography variant="body2" color="text.secondary">
                          <strong style={{ color: "#0f172a" }}>Clicks from your package: </strong>
                          {packConsumed.toLocaleString()} of {packTotal.toLocaleString()} used
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              )}

                <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", bgcolor: "#fff", height: "100%" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.06}>
                      Sponsored listing
                      </Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ mt: 0.5, color: "#0f172a" }}>
                      {campaignStatus}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", bgcolor: "#fff", height: "100%" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.06}>
                      Available for your next campaign
                    </Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ mt: 0.5, color: "#0f172a" }}>
                      {formatMoneyPaise(walletSpendablePaise)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                      Money you can put toward a new purchase. Anything still needed for this campaign is not included.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", bgcolor: "#fff", height: "100%" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.06}>
                      Reserved for this campaign
                    </Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ mt: 0.5, color: "#0f172a" }}>
                      {formatMoneyPaise(campaignFundsLockedPaise)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                      Set aside for remaining clicks until this campaign ends or uses its full package.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", bgcolor: "#fff", height: "100%" }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.06}>
                      Wallet balance
                    </Typography>
                    <Typography variant="h6" fontWeight={800} sx={{ mt: 0.5, color: "#475569" }}>
                      {formatMoneyPaise(walletBalancePaise)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                      Everything in your wallet, including what is reserved above.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", bgcolor: "#fff", height: "100%" }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.06}>
                        Clicks
                      </Typography>
                      {(walletLoading || cpcMetricsLoading) && <CircularProgress size={16} sx={{ color: "#2563eb" }} />}
                    </Stack>
                    <Typography variant="h6" fontWeight={800} sx={{ mt: 0.5, color: "#0f172a" }}>
                      {fmtInt(statClicks)}
                    </Typography>
                    {packConsumed != null && packTotal != null && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                        Package: {packConsumed.toLocaleString()} of {packTotal.toLocaleString()} clicks used
                      </Typography>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", bgcolor: "#fff", height: "100%" }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.06}>
                        Impressions
                      </Typography>
                      {(walletLoading || cpcMetricsLoading) && <CircularProgress size={16} sx={{ color: "#2563eb" }} />}
                    </Stack>
                    <Typography variant="h6" fontWeight={800} sx={{ mt: 0.5, color: "#0f172a" }}>
                      {fmtInt(statImpressions)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {campaignMetricsChartConfig && (
                <Paper
                  elevation={0}
                  sx={{
                    mt: 3,
                    p: 2.5,
                    borderRadius: 3,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#fff",
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#0f172a" }}>
                    Clicks & impressions
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2, maxWidth: 720 }}>
                    {Array.isArray(cpcMetrics?.daily) && cpcMetrics.daily.length >= 2
                      ? "Day-by-day trend for this campaign period."
                      : "Summary for this campaign period. A day-by-day view appears when results are available by day."}
                  </Typography>
                  <Box sx={{ height: 320, position: "relative", width: "100%" }}>
                    {campaignMetricsChartConfig.chartKind === "line" ? (
                      <Line data={campaignMetricsChartConfig.data} options={campaignMetricsChartConfig.options} />
                    ) : (
                      <Bar data={campaignMetricsChartConfig.data} options={campaignMetricsChartConfig.options} />
                    )}
                  </Box>
                </Paper>
              )}
            </>
          )}

        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: "#eef4fb",
        backgroundImage:
          "radial-gradient(circle at 2px 2px, rgba(37, 99, 235, 0.06) 1px, transparent 0)",
        backgroundSize: "28px 28px",
        pb: 4,
      }}
    >
      {/* —— Landing: Sponsored listing —— */}
      {!showBuilder && (
        <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 3 }, pt: { xs: 3, md: 5 }, pb: 2 }}>
          <Paper
            elevation={0}
            sx={{
              overflow: "hidden",
              borderRadius: 4,
              border: "1px solid rgba(37, 99, 235, 0.12)",
              background: "linear-gradient(145deg, #f8fbff 0%, #e8f0fc 45%, #dce8f8 100%)",
              boxShadow: "0 20px 60px rgba(15, 23, 42, 0.08)",
            }}
          >
            <Grid container>
              <Grid item xs={12} md={6} sx={{ p: { xs: 3, md: 5 }, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Chip
                  icon={<RocketIcon sx={{ fontSize: 18 }} />}
                  label="Sponsored visibility"
                  size="small"
                  sx={{
                    alignSelf: "flex-start",
                    mb: 2,
                    fontWeight: 600,
                    bgcolor: "rgba(255, 119, 0, 0.12)",
                    color: "#c2410c",
                    border: "1px solid rgba(255, 119, 0, 0.25)",
                  }}
                />
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    color: "#0f172a",
                    fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.65rem" },
                    lineHeight: 1.15,
                    letterSpacing: "-0.03em",
                  }}
                >
                  Sponsored Listing
                </Typography>
                <Typography sx={{ mt: 1.5, color: "#334155", fontWeight: 600, fontSize: { xs: "1rem", md: "1.1rem" } }}>
                  Rise in search results and win more qualified clicks in your market.
                </Typography>
                <Typography sx={{ mt: 2, color: "#64748b", maxWidth: 520, lineHeight: 1.65 }}>
                  Promote your property where travellers are already searching. You choose the dates, click package, and
                  price per click—then pay securely and go live when you are ready.
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3 }} alignItems={{ xs: "stretch", sm: "center" }}>
                      <Button
                        variant="contained"
                    size="large"
                    onClick={openBuilder}
                        sx={{
                      py: 1.5,
                      px: 3,
                      borderRadius: 2,
                          textTransform: "none",
                      fontWeight: 700,
                      fontSize: "1rem",
                      background: "linear-gradient(90deg, #f97316 0%, #ea580c 100%)",
                      boxShadow: "0 8px 24px rgba(234, 88, 12, 0.35)",
                      "&:hover": { background: "linear-gradient(90deg, #ea580c 0%, #c2410c 100%)" },
                    }}
                  >
                    Create campaign
                      </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate("/campaign/running")}
                    sx={{
                      py: 1.5,
                      px: 3,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "1rem",
                      borderColor: "rgba(37, 99, 235, 0.35)",
                      color: "#1d4ed8",
                      "&:hover": {
                        borderColor: "#2563eb",
                        bgcolor: "rgba(37, 99, 235, 0.06)",
                      },
                    }}
                  >
                    Running campaign dashboard
                  </Button>
                </Stack>

                <Divider sx={{ my: 3, borderColor: "rgba(37, 99, 235, 0.14)" }} />

                <Typography
                  variant="overline"
                  sx={{
                    color: "#64748b",
                    fontWeight: 800,
                    letterSpacing: "0.1em",
                    fontSize: "0.68rem",
                  }}
                >
                  Wallet &amp; status
                </Typography>
                <Grid container spacing={1.5} sx={{ mt: 1 }}>
                  {walletLoading ? (
                  <Grid item xs={12}>
                      <CircularProgress size={24} sx={{ color: "#2563eb" }} />
                        </Grid>
                      ) : (
                    <>
                      {[
                        {
                          label: "Available for next campaign",
                          value: `${currency} ${paiseToRupees(walletSpendablePaise).toLocaleString()}`,
                        },
                        ...(campaignFundsLockedPaise > 0
                          ? [
                              {
                                label: "Reserved for active campaign",
                                value: `${currency} ${paiseToRupees(campaignFundsLockedPaise).toLocaleString()}`,
                              },
                            ]
                          : []),
                        {
                          label: "Total wallet balance",
                          value: `${currency} ${paiseToRupees(walletBalancePaise).toLocaleString()}`,
                        },
                        {
                          label: "Campaign status",
                          value: campaignStatus,
                          isStatus: true,
                        },
                      ].map((row) => (
                        <Grid item xs={12} sm={6} key={row.label}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              borderRadius: 2.5,
                              border: "1px solid rgba(226, 232, 240, 0.95)",
                              bgcolor: "rgba(255, 255, 255, 0.88)",
                              height: "100%",
                              boxShadow: "0 2px 12px rgba(15, 23, 42, 0.04)",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontWeight: 700, display: "block", letterSpacing: 0.02 }}
                            >
                              {row.label}
                                      </Typography>
                            {row.isStatus ? (
                                      <Chip
                                        size="small"
                                label={row.value}
                                        sx={{
                                  mt: 1.25,
                                  fontWeight: 800,
                                  borderRadius: 1.5,
                                  bgcolor:
                                    row.value === "Running" ? "rgba(22, 163, 74, 0.14)" : "rgba(100, 116, 139, 0.12)",
                                  color: row.value === "Running" ? "#15803d" : "#475569",
                                }}
                              />
                            ) : (
                              <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "1.125rem", mt: 0.75 }}>
                                {row.value}
                                          </Typography>
                            )}
                          </Paper>
                        </Grid>
                      ))}
                    </>
                  )}
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  position: "relative",
                  minHeight: { xs: 280, md: 420 },
                  bgcolor: "rgba(37, 99, 235, 0.06)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 3,
                }}
              >
                                        <Box
                                          sx={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0.5,
                    background:
                      "radial-gradient(circle at 70% 30%, rgba(59, 130, 246, 0.25), transparent 50%), radial-gradient(circle at 20% 80%, rgba(249, 115, 22, 0.15), transparent 45%)",
                  }}
                />
                <Paper
                  elevation={0}
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    p: 3,
                    maxWidth: 380,
                                            borderRadius: 3,
                    border: "1px solid rgba(255,255,255,0.8)",
                    bgcolor: "rgba(255,255,255,0.92)",
                    backdropFilter: "blur(12px)",
                    animation: "campaignFloat 5s ease-in-out infinite",
                    "@keyframes campaignFloat": {
                      "0%, 100%": { transform: "translateY(0)" },
                      "50%": { transform: "translateY(-8px)" },
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <CampaignIcon sx={{ color: "#2563eb" }} />
                    <Typography fontWeight={700} color="#0f172a">
                      Preview impact
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Higher CPC and planned clicks help your listing compete for top placement during your chosen dates.
                  </Typography>
                  <Stack spacing={1.2}>
                    {[
                      { icon: <VisibilityIcon fontSize="small" />, t: "Stronger visibility on listing pages" },
                      { icon: <TrendingUpIcon fontSize="small" />, t: "Pay only for the click package you choose" },
                      { icon: <WalletIcon fontSize="small" />, t: "Fund wallet, then launch in one flow" },
                    ].map((row, i) => (
                      <Stack key={i} direction="row" spacing={1.2} alignItems="center">
                        <Box sx={{ color: "#2563eb" }}>{row.icon}</Box>
                        <Typography variant="body2" color="#334155">
                          {row.t}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Paper>

          {!isRunningCampaignsRoute && (
            <Paper
              elevation={0}
                                            sx={{
                mt: 3,
                                              borderRadius: 3,
                border: "1px solid #e2e8f0",
                bgcolor: "#fff",
                overflow: "hidden",
                boxShadow: "0 14px 44px rgba(15, 23, 42, 0.07)",
              }}
            >
              {cpcMetricsLoading && (
                <LinearProgress
                  sx={{
                    height: 3,
                    "& .MuiLinearProgress-bar": { bgcolor: "#2563eb" },
                  }}
                />
              )}
              <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ sm: "flex-start" }}
                  spacing={2}
                  sx={{ mb: 2.5 }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={800} sx={{ color: "#0f172a", letterSpacing: "-0.02em" }}>
                      How your listing is doing
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 540, lineHeight: 1.6 }}>
                      Live clicks and impressions for your property. Use the dashboard for pause, resume, and detailed
                      controls.
                    </Typography>
                  </Box>
                                    <Button
                    variant="contained"
                    onClick={() => navigate("/campaign/running")}
                                      sx={{
                                        textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 2,
                      px: 2.5,
                      flexShrink: 0,
                      background: "linear-gradient(90deg, #2563eb 0%, #1d4ed8 100%)",
                      boxShadow: "0 6px 20px rgba(37, 99, 235, 0.25)",
                      "&:hover": { background: "linear-gradient(90deg, #1d4ed8 0%, #1e40af 100%)" },
                    }}
                  >
                    Open dashboard
                                    </Button>
                </Stack>

                <Grid container spacing={2} sx={{ mb: overviewPackConsumed != null && overviewPackTotal != null ? 2 : 2.5 }}>
                  <Grid item xs={6} sm={4}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2.5,
                        background: "linear-gradient(145deg, #eff6ff 0%, #ffffff 100%)",
                        border: "1px solid #bfdbfe",
                        height: "100%",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.03}>
                        Clicks
                      </Typography>
                      <Typography variant="h5" fontWeight={800} sx={{ color: "#1d4ed8", mt: 0.5, letterSpacing: "-0.02em" }}>
                        {runningStatClicks != null ? Number(runningStatClicks).toLocaleString() : "—"}
                      </Typography>
                    </Box>
                            </Grid>
                  <Grid item xs={6} sm={4}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2.5,
                        background: "linear-gradient(145deg, #fff7ed 0%, #ffffff 100%)",
                        border: "1px solid #fed7aa",
                        height: "100%",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.03}>
                        Impressions
                      </Typography>
                      <Typography variant="h5" fontWeight={800} sx={{ color: "#c2410c", mt: 0.5, letterSpacing: "-0.02em" }}>
                        {runningStatImpressions != null ? Number(runningStatImpressions).toLocaleString() : "—"}
                      </Typography>
                    </Box>
                    </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2.5,
                        bgcolor: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        height: "100%",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.03}>
                        Campaign status
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          label={campaignStatus}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            bgcolor:
                              campaignStatus === "Running" ? "rgba(22, 163, 74, 0.14)" : "rgba(100, 116, 139, 0.12)",
                            color: campaignStatus === "Running" ? "#15803d" : "#475569",
                          }}
                        />
                    </Box>
                    </Box>
                  </Grid>
                </Grid>

                {overviewPackConsumed != null && overviewPackTotal != null && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    Click package:{" "}
                    <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
                      {overviewPackConsumed.toLocaleString()} of {overviewPackTotal.toLocaleString()}
                    </Box>{" "}
                    clicks used
                          </Typography>
                )}

                {campaignMetricsChartConfig && (
                  <>
                    <Divider sx={{ my: 1, borderColor: "#f1f5f9" }} />
                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#0f172a", mb: 0.5 }}>
                      Clicks & impressions
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 640 }}>
                      {Array.isArray(cpcMetrics?.daily) && cpcMetrics.daily.length >= 2
                        ? "Trend by day for your current period."
                        : "Summary totals for your current campaign window."}
                    </Typography>
                    <Box sx={{ height: 280, position: "relative", width: "100%" }}>
                      {campaignMetricsChartConfig.chartKind === "line" ? (
                        <Line data={campaignMetricsChartConfig.data} options={campaignMetricsChartConfig.options} />
                      ) : (
                        <Bar data={campaignMetricsChartConfig.data} options={campaignMetricsChartConfig.options} />
                      )}
                        </Box>
                  </>
                )}
              </Box>
            </Paper>
          )}

          <Grid id="sl-learn-more" container spacing={2.5} sx={{ mt: 3 }}>
            {[
              {
                title: "What is Sponsored Listing?",
                body: "A paid visibility boost for your property on TabTrips. You set a run window, target clicks, and CPC within allowed limits—so you stay in control of spend and timing.",
                icon: <CampaignIcon sx={{ color: "#2563eb" }} />,
              },
              {
                title: "How does it help?",
                body: "Better placement means more eyeballs on your rooms when demand is high. Pair that with sharp photos and rates to improve conversion from views to bookings.",
                icon: <TrendingUpIcon sx={{ color: "#ea580c" }} />,
              },
              {
                title: "How do I start?",
                body: "Tap Create campaign, review your quote, pay through Razorpay, then launch. You can pause anytime from this same page.",
                icon: <RocketIcon sx={{ color: "#16a34a" }} />,
              },
            ].map((card) => (
              <Grid item xs={12} md={4} key={card.title}>
                <Paper
                  elevation={0}
                  sx={{
                    height: "100%",
                    p: 3,
                    borderRadius: 3,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#fff",
                    boxShadow: "0 4px 20px rgba(15, 23, 42, 0.04)",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
                    },
                  }}
                >
                  <Box sx={{ mb: 1.5 }}>{card.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                    {card.body}
                  </Typography>
                      </Paper>
                  </Grid>
            ))}
                </Grid>
        </Box>
      )}

      {/* —— Builder (after Create campaign) —— */}
      {showBuilder && (
        <>
        <Box
          id="campaign-builder"
          sx={{
            maxWidth: 1200,
            mx: "auto",
            px: { xs: 2, md: 3 },
            pt: { xs: 2, md: 3 },
            pb: { xs: 10, sm: 11 },
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
              Create campaign
            </Typography>
            <Button onClick={closeBuilder} sx={{ textTransform: "none", fontWeight: 600, color: "#2563eb" }}>
              Back to overview
            </Button>
          </Stack>

          <Grid container spacing={2.5}>
            {/* Stepper rail */}
            <Grid item xs={12} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  bgcolor: "#fff",
                  position: { md: "sticky" },
                  top: { md: 88 },
                }}
              >
                <Stepper activeStep={activeStep} orientation={isMobile ? "horizontal" : "vertical"} sx={{ mb: isMobile ? 0 : 0 }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel
                        sx={{
                          "& .MuiStepLabel-label": { fontWeight: 600, color: "#64748b" },
                          "& .MuiStepLabel-label.Mui-active": { color: "#1d4ed8" },
                          "& .MuiStepLabel-label.Mui-completed": { color: "#0f766e" },
                        }}
                      >
                        {label}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
                {!isMobile && (
                  <Box sx={{ mt: 2, p: 1.75, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                    <Typography variant="overline" sx={{ fontWeight: 700, color: "#64748b", letterSpacing: "0.06em", fontSize: "0.65rem" }}>
                      Selected property
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                      Hotel ID {hotelId}
                    </Typography>
                    <Divider sx={{ my: 1.25 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Campaign status
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a", mt: 0.25 }}>
                      {campaignStatus}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mt: 1, display: "block" }}>
                      Available for checkout (next campaign)
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: "#0f172a", mt: 0.25 }}>
                      {walletLoading
                        ? "…"
                        : `${currency} ${paiseToRupees(walletSpendablePaise).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`}
                    </Typography>
                    {!walletLoading && campaignFundsLockedPaise > 0 && (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mt: 0.75, display: "block" }}>
                          Reserved for this campaign
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block", color: "#475569" }}>
                          {currency}{" "}
                          {paiseToRupees(campaignFundsLockedPaise).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>
                      </>
                    )}
                    {!walletLoading && (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mt: 0.75, display: "block" }}>
                          Total wallet
                        </Typography>
                        <Typography variant="caption" sx={{ display: "block", color: "#475569" }}>
                          {currency}{" "}
                          {paiseToRupees(walletBalancePaise).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>
                      </>
                    )}
                  </Box>
                )}
                    </Paper>
                  </Grid>

            {/* Main wizard */}
            <Grid item xs={12} md={9}>
              <Stack spacing={2}>
                {activeStep >= 1 && (
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #e2e8f0", bgcolor: "#fafafa" }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CheckCircleIcon sx={{ color: "#0d9488", fontSize: 22 }} />
                        <Typography fontWeight={700}>Campaign details</Typography>
                      </Stack>
                      <Button size="small" onClick={() => setActiveStep(0)} sx={{ textTransform: "none", fontWeight: 600 }}>
                        Edit
                      </Button>
                    </Stack>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="caption" color="text.secondary">
                          Duration
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {startDate && endDate
                            ? `${formatDateDisplay(startDate)} – ${formatDateDisplay(endDate)}${
                                inclusiveDayCount > 0 ? ` (${inclusiveDayCount} days)` : ""
                              }`
                            : "—"}
                        </Typography>
                </Grid>
                      <Grid item xs={6} sm={3} md={2}>
                        <Typography variant="caption" color="text.secondary">
                          Clicks
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {Number(clicks).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3} md={2}>
                        <Typography variant="caption" color="text.secondary">
                          CPC
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {currency} {Number(cpc).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={12} md={5}>
                        <Typography variant="caption" color="text.secondary">
                          Campaign name
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {campaignName.trim() || "—"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                {/* Step 0 */}
                {activeStep === 0 && (
                  <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden", bgcolor: "#fff" }}>
                    <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #f1f5f9" }}>
                      <Typography variant="subtitle1" fontWeight={800}>
                        Campaign details
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Set your run window, bid, and volume. Then review cost before payment.
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2.5 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={6}>
                            <CampaignDatePicker
                              label="Campaign start"
                              value={startDateObj}
                              onChange={(newValue) => {
                                if (!newValue) {
                                  setStartDate("");
                                  setEndDate("");
                                  return;
                                }
                                const ymd = toYMDLocal(newValue);
                                const today = todayLocalYMD();
                                if (ymd < today) return;
                                setStartDate(ymd);
                                const minEnd = addDaysYMD(ymd, MIN_CAMPAIGN_SPAN_DAYS);
                                if (endDate && endDate < minEnd) setEndDate("");
                              }}
                              minDate={todayAsDate || undefined}
                              inputFormat="dd MMM yyyy"
                              disablePast
                              renderInput={(params) => (
                                <TextField {...params} fullWidth InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />
                              )}
                            />
                    </Grid>
                        <Grid item xs={12} sm={6}>
                            <CampaignDatePicker
                              label="Campaign end"
                              value={endDateObj}
                              onChange={(newValue) => {
                                if (!newValue) {
                                  setEndDate("");
                                  return;
                                }
                                const ymd = toYMDLocal(newValue);
                                if (!startDate || !minEndDateStr || ymd < minEndDateStr) return;
                                setEndDate(ymd);
                              }}
                              minDate={minEndDateObj || undefined}
                              disabled={!startDate}
                              inputFormat="dd MMM yyyy"
                              renderInput={(params) => (
                                <TextField {...params} fullWidth InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />
                              )}
                            />
                        </Grid>

                        <Grid item xs={12}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
                            <Typography fontWeight={700}>
                              Cost per click (CPC)
                              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 500 }}>
                                Bid higher to compete for placement
                              </Typography>
                            </Typography>
                            <Chip
                              label={`${currency} ${cpc}`}
                              size="small"
                              sx={{ fontWeight: 700, bgcolor: "#dbeafe", color: "#1e40af" }}
                            />
                          </Stack>
                          <Slider
                            value={Number(cpc)}
                            min={MIN_CPC}
                            max={MAX_CPC}
                            step={1}
                            onChange={(_, v) => setCpc(v)}
                            valueLabelDisplay="on"
                            sx={{ mt: 1, color: "#2563eb" }}
                          />
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                              {currency} {MIN_CPC}
                            </Typography>
                            {cpc === RECOMMENDED_CPC && (
                              <Typography variant="caption" sx={{ color: "#16a34a", fontWeight: 700 }}>
                                Recommended
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {currency} {MAX_CPC}
                            </Typography>
                              </Stack>
                          <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "#eff6ff", borderColor: "#bfdbfe" }}>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                              <LightbulbIcon sx={{ color: "#2563eb", mt: 0.25 }} />
                              <Box component="ul" sx={{ m: 0, pl: 2, color: "#1e3a5f", fontSize: "0.875rem", lineHeight: 1.6 }}>
                                <li>Balanced CPC usually gives a steady mix of clicks and return on spend.</li>
                                <li>Raising CPC can increase delivery; lowering it may reduce volume.</li>
                                <li>Totals update as you change clicks or cost per click.</li>
                            </Box>
                            </Stack>
                          </Paper>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography fontWeight={700} sx={{ mb: 1 }}>
                            Clicks
                          </Typography>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={4}>
                    <TextField
                                fullWidth
                                type="number"
                                label="Target clicks"
                                value={clicks}
                                onChange={(e) =>
                                  setClicks(Math.min(MAX_CLICKS, Math.max(MIN_CLICKS, Number(e.target.value) || MIN_CLICKS)))
                                }
                                inputProps={{ min: MIN_CLICKS, max: MAX_CLICKS }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                              <Slider
                                value={Number(clicks)}
                                min={MIN_CLICKS}
                                max={MAX_CLICKS}
                                step={5}
                                onChange={(_, v) => setClicks(v)}
                                valueLabelDisplay="on"
                                sx={{ color: "#2563eb" }}
                              />
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="caption" color="text.secondary">
                                  {MIN_CLICKS.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {MAX_CLICKS.toLocaleString()}
                                </Typography>
                  </Stack>
                            </Grid>
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            required
                            label="Campaign name"
                            placeholder="e.g. Summer push – city centre"
                            value={campaignName}
                            onChange={(e) => setCampaignName(e.target.value)}
                            error={showCampaignNameRequired}
                            helperText={
                              showCampaignNameRequired
                                ? "Required — add a short name so you can recognize this campaign."
                                : " "
                            }
                            FormHelperTextProps={{ sx: { minHeight: "1.25em", m: 0, mt: 0.5 } }}
                          />
                            </Grid>
                        </Grid>
                      </LocalizationProvider>

                      <Divider sx={{ my: 3 }} />

                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight={800}>
                          Cost of campaign
                        </Typography>
                        {quoteLoading && <CircularProgress size={18} sx={{ color: "#2563eb" }} />}
                      </Stack>
                      <TableContainer sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
                        <Table size="small">
                              <TableHead>
                            <TableRow sx={{ bgcolor: "#f8fafc" }}>
                              <TableCell sx={{ fontWeight: 700 }}>Segment</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                Clicks
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                CPC
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                Cost
                              </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                            <TableRow>
                              <TableCell>All travellers</TableCell>
                              <TableCell align="right">{Number(clicks).toLocaleString()}</TableCell>
                              <TableCell align="right">
                                {currency} {Number(cpc).toLocaleString()}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                {currency} {quoteSummary.base_rupees.toLocaleString()}
                              </TableCell>
                                  </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 700 }}>Overall</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {Number(clicks).toLocaleString()}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {currency} {Number(cpc).toLocaleString()}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {currency} {quoteSummary.base_rupees.toLocaleString()}
                              </TableCell>
                            </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                      {walletBalancePaise > 0 && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            borderRadius: 2,
                            border: "1px solid #e2e8f0",
                            bgcolor: "#f8fafc",
                          }}
                        >
                          <Typography variant="caption" fontWeight={700} color="text.secondary" letterSpacing={0.06}>
                            Wallet (this property)
                          </Typography>
                          <Typography variant="subtitle2" fontWeight={800} sx={{ color: "#0f172a", mt: 0.75 }}>
                            Available at checkout: {formatMoneyPaise(walletSpendablePaise)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Total in wallet {formatMoneyPaise(walletBalancePaise)}
                            {campaignFundsLockedPaise > 0 && (
                              <span>
                                {" "}
                                · Reserved for your current campaign {formatMoneyPaise(campaignFundsLockedPaise)}
                              </span>
                            )}
                          </Typography>
                          {walletSpendablePaise <= 0 && campaignFundsLockedPaise > 0 && (
                            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                              Your wallet cannot reduce this payment while funds are reserved for your active campaign.
                              They will free up when that campaign finishes or uses all its clicks.
                            </Typography>
                          )}
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={useWalletForCampaign}
                                onChange={(e) => setUseWalletForCampaign(e.target.checked)}
                                color="primary"
                                disabled={walletSpendablePaise <= 0}
                              />
                            }
                            label={
                              <Typography variant="body2" color="text.secondary">
                                Apply wallet balance to this purchase
                              </Typography>
                            }
                            sx={{ alignItems: "flex-start", ml: 0, mr: 0, mt: 1 }}
                          />
                          {useWalletForCampaign && walletDeductionPaise > 0 && (
                            <Stack spacing={1} sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #e2e8f0" }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="text.secondary">
                                  Package total
                                </Typography>
                                <Typography variant="body2" fontWeight={700}>
                                  {formatMoneyPaise(quoteSummary.total_paise)}
                                </Typography>
                              </Stack>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" color="text.secondary">
                                  Wallet credit (max available for this package)
                                </Typography>
                                <Typography variant="body2" fontWeight={700} color="success.main">
                                  −{formatMoneyPaise(walletDeductionPaise)}
                                </Typography>
                              </Stack>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2" fontWeight={800}>
                                  Estimated card / UPI charge
                                </Typography>
                                <Typography variant="body2" fontWeight={800} color="primary.main">
                                  {formatMoneyPaise(amountDuePaise)}
                                </Typography>
                              </Stack>
                            </Stack>
                          )}
                        </Box>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                        {baseCostLabel}
                      </Typography>
                  </Box>
                  </Paper>
                )}

                {/* Step 1 Payments */}
                {activeStep === 1 && (
                  <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", bgcolor: "#fff" }}>
                    <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #f1f5f9" }}>
                      <Typography variant="subtitle1" fontWeight={800}>
                        Payments
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Review billing, then pay with card or UPI. Coupons are not applied at this time.
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2.5 }}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                          <Typography fontWeight={700} color="text.secondary" variant="body2">
                            Billing overview
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {paymentLineItems.fromServer
                              ? "Final figures from checkout."
                              : walletDeductionPaise > 0
                                ? "Estimates until payment. Final total is confirmed at checkout."
                                : "Estimates until payment is complete."}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={8}>
                          <Box
                            sx={{
                              borderLeft: { md: "4px solid #2563eb" },
                              pl: { md: 2 },
                            }}
                          >
                            <Typography variant="overline" fontWeight={800} color="primary" letterSpacing={0.08}>
                              Price breakup
                            </Typography>
                            <Stack spacing={1} sx={{ mt: 1 }}>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" color="text.secondary">
                                  {Number(clicks).toLocaleString()} clicks cost (base)
                                </Typography>
                                <Typography fontWeight={600}>
                                  {currency}{" "}
                                  {paymentLineItems.baseRupees.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </Typography>
                  </Stack>
                              <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2" color="text.secondary">
                                  GST
                                </Typography>
                                <Typography fontWeight={600}>
                                  {currency}{" "}
                                  {paymentLineItems.gstRupees.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </Typography>
                              </Stack>
                              <Divider />
                              {paymentLineItems.walletCreditPaise > 0 ? (
                                <>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography fontWeight={700}>
                                      Subtotal {!paymentLineItems.fromServer ? "(estimate)" : ""}
                                    </Typography>
                                    <Typography fontWeight={700} fontSize="1.05rem">
                                      {formatMoneyPaise(paymentLineItems.payPaise)}
                                    </Typography>
                                  </Stack>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                      Wallet credit applied
                                    </Typography>
                                    <Typography fontWeight={700} color="success.main">
                                      −{formatMoneyPaise(paymentLineItems.walletCreditPaise)}
                                    </Typography>
                                  </Stack>
                                  <Divider />
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography fontWeight={800}>
                                      {paymentLineItems.residualPaise > 0 ? "Razorpay (residual)" : "Card / UPI"}
                                    </Typography>
                                    <Typography fontWeight={800} fontSize="1.15rem" color="primary.main">
                                      {formatMoneyPaise(paymentLineItems.residualPaise)}
                                    </Typography>
                                  </Stack>
                                </>
                              ) : (
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography fontWeight={800}>Total (payable)</Typography>
                                  <Typography fontWeight={800} fontSize="1.15rem">
                                    {formatMoneyPaise(paymentLineItems.payPaise)}
                                  </Typography>
                                </Stack>
                              )}
                            </Stack>
                      </Box>
                      </Grid>
                      </Grid>

                      <Divider sx={{ my: 3 }} />

                      <Typography fontWeight={700} sx={{ mb: 1 }}>
                        Payment
                      </Typography>
                      {!paymentConfirmed && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Pay securely with card or UPI. If your wallet covers the full amount, you may be charged
                          nothing extra.
                        </Typography>
                      )}
                      {!paymentConfirmed && (
                        <Button
                          variant="contained"
                          size="large"
                          disabled={paymentLoading || !dateRangeValid}
                          onClick={handleCreateOrderAndPay}
                          startIcon={paymentLoading ? <CircularProgress size={20} color="inherit" /> : <WalletIcon />}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: 2,
                            px: 3,
                            background: "linear-gradient(90deg, #f97316 0%, #ea580c 100%)",
                            "&:hover": { background: "linear-gradient(90deg, #ea580c 0%, #c2410c 100%)" },
                          }}
                        >
                          {paymentLoading
                            ? "Opening checkout…"
                            : paymentLineItems.residualPaise <= 0 &&
                                useWalletForCampaign &&
                                paymentLineItems.walletCreditPaise > 0
                              ? "Continue (wallet may cover total)"
                              : `Pay ${formatMoneyPaise(
                                  paymentLineItems.walletCreditPaise > 0
                                    ? paymentLineItems.residualPaise
                                    : paymentLineItems.payPaise
                                )}`}
                        </Button>
                      )}
                      {paymentConfirmed && (
                        <Alert severity="success" sx={{ mt: 0, borderRadius: 2 }}>
                          Payment received. Continue to the <strong>Launch</strong> step to accept the terms and start
                          your sponsored listing, or open{" "}
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            fontWeight={600}
                            onClick={() => navigate("/campaign/running")}
                            sx={{ verticalAlign: "baseline", p: 0, minWidth: 0 }}
                          >
                            Running campaigns
                          </Link>{" "}
                          for a snapshot of delivery and wallet.
                        </Alert>
                      )}
                  </Box>
                  </Paper>
                )}

                {/* Step 2 Launch */}
                {activeStep === 2 && (
                  <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", bgcolor: "#fff" }}>
                    <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid #f1f5f9" }}>
                      <Typography variant="subtitle1" fontWeight={800}>
                        Launch
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Accept the terms, then start your campaign. You can pause it anytime. After you start, view
                        performance under Running campaigns in the sidebar.
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2.5 }}>
                      {paymentConfirmed && campaignStatus === "Running" && (
                        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                          Your listing is already being promoted. Use <strong>Pause</strong> if you need to stop delivery,
                          or open <strong>Running campaigns</strong> in the sidebar to review activity.
                        </Alert>
                      )}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="body2" color="text.secondary">
                            I confirm the charges above and agree to the{" "}
                            <Link href="/support" underline="hover" fontWeight={600}>
                              terms
                            </Link>{" "}
                            for sponsored listings.
                          </Typography>
                        }
                      />
                    </Box>
                  </Paper>
                )}

                {/* Side summary on wide screens could go here; compact checklist below */}
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px dashed #cbd5e1", bgcolor: "#f8fafc" }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                    Progress
            </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <ReceiptLongIcon fontSize="small" color={canGoStep2 ? "primary" : "disabled"} />
                      <Typography variant="body2">Pricing</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <WalletIcon fontSize="small" color={paymentConfirmed ? "primary" : "disabled"} />
                      <Typography variant="body2">Paid</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <PlayIcon fontSize="small" color={campaignStatus === "Running" ? "success" : "disabled"} />
                      <Typography variant="body2">Live</Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Paper
          component="footer"
          elevation={12}
                  sx={{
            position: "fixed",
            bottom: 0,
            left: isAppMobile ? 0 : MAIN_SIDEBAR_OFFSET_PX,
            right: 0,
            zIndex: 1090,
            borderTop: "1px solid #e2e8f0",
            bgcolor: "rgba(255,255,255,0.97)",
            backdropFilter: "blur(10px)",
            boxShadow: "0 -8px 32px rgba(15, 23, 42, 0.08)",
            py: 1.5,
            px: { xs: 2, sm: 3 },
            boxSizing: "border-box",
          }}
        >
          <Box
            sx={{
              maxWidth: 1200,
              mx: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Button
              onClick={activeStep === 0 ? closeBuilder : () => setActiveStep((s) => Math.max(0, s - 1))}
              sx={{ textTransform: "none", fontWeight: 600, color: "#2563eb" }}
            >
              {activeStep === 0 ? "Cancel" : "Back"}
            </Button>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end", flex: 1 }}>
              {activeStep === 0 && (
                <Button
                  variant="contained"
                  disabled={!canGoStep2}
                  onClick={() => setActiveStep(1)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    px: 3,
                    minHeight: 44,
                    background: "linear-gradient(90deg, #f97316 0%, #ea580c 100%)",
                  }}
                >
                  Next
                </Button>
              )}
              {activeStep === 1 && !paymentConfirmed && (
                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 280, textAlign: "right" }}>
                  Complete payment above to continue.
                </Typography>
              )}
              {activeStep === 1 && paymentConfirmed && (
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(2)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                    px: 3,
                    minHeight: 44,
                    background: "linear-gradient(90deg, #f97316 0%, #ea580c 100%)",
                  }}
                >
                  Confirm
                </Button>
              )}
              {activeStep === 2 && (
                <>
                  <Button
                    variant="outlined"
                    color="warning"
                    disabled={pauseLoading}
                    onClick={handlePauseCampaign}
                    startIcon={pauseLoading ? <CircularProgress size={18} color="inherit" /> : <PauseIcon />}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, minHeight: 44 }}
                  >
                    {pauseLoading ? "Pausing…" : "Pause"}
                  </Button>
            <Button
              variant="contained"
                    disabled={startLoading || !paymentConfirmed || !termsAccepted}
                    onClick={handleStartCampaign}
                    startIcon={startLoading ? <CircularProgress size={18} color="inherit" /> : <PlayIcon />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 2,
                      minHeight: 44,
                      bgcolor: "#15803d",
                      "&:hover": { bgcolor: "#166534" },
                    }}
                  >
                    {startLoading ? "Starting…" : "Confirm & start"}
            </Button>
                </>
              )}
            </Box>
          </Box>
        </Paper>
        </>
      )}
      </Box>
  );
};

export default CampaignPage;
